import React, { useCallback, useContext, useMemo, useState } from 'react'

import { generateColors } from '@/utils/xlsxHelpers'

const PlanningContext = React.createContext({
    settingsType: '',
    setSettingsType: () => {},
    settingTypeOptions: [{ label: '', value: '' }],
    isAthleteSettings: true,
    isVolunteerSettings: true,
    readPlanning: () => {},
    divisionsIdsAndNames: [],
    rolesIdsAndNames: [],
    divisionColorObject: { ['']: [] },
    readOrdersCheckInStates: ({ orders, volunteerOrders }, totalParticipations, totalVolunteers) => {},
    ordersCheckInStates: [],
    volunteerOrdersCheckInStates: [],
    ordersCheckInInfo: { count: 0, absentsCount: 0, checkedInCount: 0 },
    volunteerOrdersCheckInInfo: { count: 0, absentsCount: 0, checkedInCount: 0 },
    readCompetition: (competition) => {},
    participationsAthletesObject: { ['']: [] },
})

const SETTINGS_TYPE_OPTIONS = ['Athletes', 'Volunteers'].map((o) => ({ label: o, value: o }))

export const PlanningContextProvider = (props) => {
    const [settingsType, setSettingsType] = useState(SETTINGS_TYPE_OPTIONS[0].value)

    const [divisionsIdsAndNames, setDivisionsIdsAndNames] = useState([])
    const [rolesIdsAndNames, setRolesIdsAndNames] = useState([])

    const [divisionColorObject, setDivisionColorObject] = useState()

    const [ordersCheckInStates, setOrdersCheckInStates] = useState()
    const [volunteerOrdersCheckInStates, setVolunteerOrdersCheckInStates] = useState()

    const [ordersCheckInInfo, setOrdersCheckInInfo] = useState()
    const [volunteerOrdersCheckInInfo, setVolunteerOrdersCheckInInfo] = useState()

    const [participationsAthletesObject, setParticipationsAthletesObject] = useState()

    const readPlanning = useCallback((planningProp) => {
        setDivisionsIdsAndNames(
            planningProp.divisions.map((item) => ({
                _id: item._id,
                name: item.name,
            })),
        )
        setRolesIdsAndNames(
            planningProp.roles.map((item) => ({
                _id: item._id,
                name: item.name,
            })),
        )

        const colors = generateColors(planningProp.divisions.length)
        setDivisionColorObject(planningProp.divisions.reduce((t, o, i) => ({ ...t, [o._id]: colors[i] }), {}))
    }, [])

    const readOrdersCheckInStates = useCallback((
        { orders, volunteerOrders },
        totalParticipations,
        totalVolunteers,
        competitionDivisions,
    ) => {
        competitionDivisions
            .flatMap((o) => o.participants)
            .filter((o) => o._order)
            .map((o) => {
                const foundIndex = orders.findIndex((b) => b._id === o._order)
                const found = orders[foundIndex]
                if (found && found.participant_id !== o._id) {
                    console.log('wrong order participant_id found', {
                        findThis: found._id,
                        updateThis: found.participant_id,
                        withThis: o._id,
                    })
                    orders[foundIndex] = { ...found, participant_id: o._id, o }
                }
            })

        setOrdersCheckInStates(orders.reduce((t, o) => ({ ...t, [o.participant_id]: o }), {}))
        setVolunteerOrdersCheckInStates(volunteerOrders.reduce((t, o) => ({ ...t, [o._id]: o }), {}))

        setOrdersCheckInInfo({
            pendingCount: totalParticipations - orders.length,
            checkedInAndAbsentsCount: orders.length,
            absentsCount: orders.filter((o) => o.checkInState === 'absent').length,
            checkedInCount: orders.filter((o) => o.checkInState === 'checked in').length,
        })
        setVolunteerOrdersCheckInInfo({
            pendingCount: totalVolunteers - volunteerOrders.length,
            checkedInAndAbsentsCount: volunteerOrders.length,
            absentsCount: volunteerOrders.filter((o) => o.checkInState === 'absent').length,
            checkedInCount: volunteerOrders.filter((o) => o.checkInState === 'checked in').length,
        })
    }, [])

    const readCompetition = useCallback((competition) => {
        setParticipationsAthletesObject(
            competition.divisions
                .flatMap((o) => o.participants)
                .reduce((t, o) => ({ ...t, [o._id]: o.athletes.map((b) => b.username) }), {}),
        )
    }, [])

    // stable value: consumers only re-render when one of these actually changes
    const value = useMemo(
        () => ({
            settingsType,
            setSettingsType,
            settingTypeOptions: SETTINGS_TYPE_OPTIONS,
            isAthleteSettings: settingsType === 'Athletes',
            isVolunteerSettings: settingsType === 'Volunteers',
            readPlanning,
            divisionsIdsAndNames,
            rolesIdsAndNames,
            divisionColorObject,
            readOrdersCheckInStates,
            ordersCheckInStates,
            volunteerOrdersCheckInStates,
            noAthleteCheckedInsOrAbsents: ordersCheckInInfo?.checkedInAndAbsentsCount === 0,
            noVolunteerCheckedInsOrAbsents: volunteerOrdersCheckInInfo?.checkedInAndAbsentsCount === 0,
            ordersCheckInInfo,
            volunteerOrdersCheckInInfo,
            readCompetition,
            participationsAthletesObject,
        }),
        [
            settingsType,
            readPlanning,
            divisionsIdsAndNames,
            rolesIdsAndNames,
            divisionColorObject,
            readOrdersCheckInStates,
            ordersCheckInStates,
            volunteerOrdersCheckInStates,
            ordersCheckInInfo,
            volunteerOrdersCheckInInfo,
            readCompetition,
            participationsAthletesObject,
        ],
    )

    return <PlanningContext.Provider value={value}>{props.children}</PlanningContext.Provider>
}

export default PlanningContext

export const usePlanning = () => useContext(PlanningContext)
